import { createPixiApp } from './app/createPixiApp';
import { bindOverlayResize } from './app/resize';
import { AudioDirector } from './engine/audio/AudioDirector';
import { CameraDirector } from './engine/camera/CameraDirector';
import { GameEventBus } from './engine/events/GameEventBus';
import { Renderer } from './engine/render/Renderer';
import { LayerManager } from './engine/render/LayerManager';
import { GameWorld } from './engine/world/GameWorld';
import { loadGameAssets } from './game/assets/GameAssets';
import { normalizeBackgroundMusic, normalizeCameraZoomStep, normalizeRuntimeActivityEvents, normalizeRuntimeBuildings, normalizeRuntimeCharacters, normalizeRuntimeResources, readRuntimeConfig } from './game/runtimeConfig';
import { OverlayHUD } from './overlay/OverlayHUD';
import { normalizeWorldEvent, WorldEventEffects } from './overlay/WorldEventEffects';

async function bootstrap(): Promise<void> {
  let root = document.querySelector<HTMLDivElement>('#app, [data-sk-pixi-root]');
  if (!root) {
    root = document.createElement('div');
    root.id = 'app';
    root.dataset.skPixiRoot = 'true';
    document.body.prepend(root);
  }
  document.body.classList.add('sk-pixi-overlay-active');

  const app = await createPixiApp();
  const assets = await loadGameAssets();
  const layers = new LayerManager();
  const params = new URLSearchParams(window.location.search);
  const config = readRuntimeConfig(params);
  const world = new GameWorld({
    kingdomId: config.kingdomId,
    visualPolish: config.visualPolish,
    waterMode: config.waterMode,
    season: config.season,
    worldTime: config.worldTime,
    growthLevel: config.growthLevel,
    characters: config.characters,
    buildings: config.buildings,
    activityEvents: config.activityEvents,
    resources: config.resources,
  });
	window.StreamKingdomsDebugState = () => ({
		visualPolish: config.visualPolish,
		waterMode: config.waterMode,
		resources: world.getSnapshot().resources,
		season: {
			year: world.getSnapshot().season.year,
			currentSeason: world.getSnapshot().season.currentSeason,
			seasonDay: world.getSnapshot().season.seasonDay,
		},
		units: world.getSnapshot().units.map((unit) => ({
			id: unit.id,
			name: unit.label ?? unit.id,
			x: unit.x,
			y: unit.y,
			activity: unit.activity ?? 'idle',
			hidden: Boolean(unit.hidden),
			roadOffsetX: unit.roadOffsetX ?? 0,
			roadOffsetY: unit.roadOffsetY ?? 0,
		})),
		buildings: world.getSnapshot().buildings.map((building) => ({
			id: building.id,
			kind: building.kind,
			x: building.x,
			y: building.y,
			activity: building.activity ?? 'idle',
			activeWorkers: building.activeWorkers ?? 0,
			activeTask: building.activeTask ?? '',
		})),
		decorations: world.getSnapshot().decorations.map((decoration) => ({
			id: decoration.id,
			kind: decoration.kind,
			x: decoration.x,
			y: decoration.y,
		})),
		navigation: world.getNavigationDebug(),
	});
  const eventBus = new GameEventBus();
  const renderer = new Renderer(layers, assets, {
    visualPolish: config.visualPolish,
    showAllLabels: config.showAllLabels,
    waterMode: config.waterMode,
    branding: config.branding,
  });
  window.StreamKingdomsCastleState = () => renderer.getCastleState();
  const camera = new CameraDirector(layers.root, { visualPolish: config.visualPolish, manualZoomStep: config.cameraZoomStep });
  window.StreamKingdomsCameraState = () => camera.getState();
  const hud = new OverlayHUD(layers.uiLayer, { showDebug: config.showDebug, visualPolish: config.visualPolish });
  const worldEventEffects = new WorldEventEffects(layers.effectsLayer, layers.uiLayer, layers.entityLayer, assets);
  worldEventEffects.setEvent(config.worldEvent);
  const audio = new AudioDirector(config.backgroundMusic);
  audio.start();
  audio.setWorldEvent(config.worldEvent);
  window.StreamKingdomsAudioState = () => audio.getState();

  let characterSignature = JSON.stringify(config.characters);
  window.StreamKingdomsUpdateCharacters = (value: unknown): void => {
    const characters = normalizeRuntimeCharacters(value);
    const nextSignature = JSON.stringify(characters);
    if (!characters.length || nextSignature === characterSignature) return;
    characterSignature = nextSignature;
    world.setCharacters(characters);
    renderer.render(world);
  };
  let buildingSignature = JSON.stringify(config.buildings);
  window.StreamKingdomsUpdateBuildings = (value: unknown): void => {
    const buildings = normalizeRuntimeBuildings(value);
    const nextSignature = JSON.stringify(buildings);
    if (nextSignature === buildingSignature) return;
    buildingSignature = nextSignature;
    world.setRuntimeBuildings(buildings);
    renderer.render(world);
    camera.syncSnapshot(world.getSnapshot());
  };
  let activitySignature = JSON.stringify(config.activityEvents);
  window.StreamKingdomsUpdateActivityEvents = (value: unknown): void => {
    const events = normalizeRuntimeActivityEvents(value);
    const nextSignature = JSON.stringify(events);
    if (nextSignature === activitySignature) return;
    activitySignature = nextSignature;
    world.setActivityEvents(events);
    hud.update(world.getSnapshot(), app.ticker);
  };
  window.StreamKingdomsUpdateWorldEvent = (value: unknown): void => {
    const event = normalizeWorldEvent(value);
    worldEventEffects.setEvent(event);
    audio.setWorldEvent(event);
  };
  window.StreamKingdomsUpdateAudio = (value: unknown): void => {
    audio.setBackgroundMusic(normalizeBackgroundMusic(value));
  };
  window.StreamKingdomsUpdateResources = (value: unknown): void => {
    world.setResources(normalizeRuntimeResources(value));
    hud.update(world.getSnapshot(), app.ticker);
  };
	window.StreamKingdomsUpdateCamera = (value: unknown): void => {
		camera.setManualZoomStep(normalizeCameraZoomStep(value));
	};

  app.stage.addChild(layers.root, layers.uiLayer);
  renderer.render(world);
  camera.focusSnapshot(world.getSnapshot());
  hud.update(world.getSnapshot(), app.ticker);
  eventBus.subscribe((event) => {
    world.applyEvent(event);
    camera.syncSnapshot(world.getSnapshot());
    camera.handleEvent(event);
    renderer.render(world);
    hud.update(world.getSnapshot(), app.ticker);
  });
	let debugElapsedMs = 0;
  app.ticker.add((ticker) => {
    world.update(ticker.deltaMS);
    renderer.render(world);
    camera.syncSnapshot(world.getSnapshot());
    camera.update(ticker);
    hud.update(world.getSnapshot(), ticker);
	worldEventEffects.update(ticker.deltaMS, world.getSnapshot().map);
	debugElapsedMs += ticker.deltaMS;
	if (debugElapsedMs >= 500) {
		debugElapsedMs = 0;
		root.dataset.skDebugState = JSON.stringify(window.StreamKingdomsDebugState?.() ?? {});
		root.dataset.skCameraState = JSON.stringify(camera.getState());
		root.dataset.skAudioState = JSON.stringify(audio.getState());
	}
  });
  root.appendChild(app.canvas);
  bindOverlayResize(app);
}

bootstrap().catch((error: unknown) => {
  console.error('Stream Kingdoms Pixi Bootstrap fehlgeschlagen:', error);
});
